/**
 * 都道府県 → 粗い中心座標のテーブル。
 *
 * 用途: nearby mode で「現在地に近い店舗」を優先的に resolve するため、
 * 各店舗の `pref` 文字列から粗い位置を引いて並び替える (ソート用)。
 *
 * 正確な centroid ではなく「県庁所在地付近 / 地方の代表都市」レベルの
 * 近似座標。十数 km 単位の誤差があっても、progressive resolve の先頭 200 件を
 * 埋める用途としては十分機能する。
 *
 * 47 都道府県全てを網羅。tabelog.json の `pref` は県名表記ゆれが無いので
 * そのままキーに使える。未知キー用のフォールバックは呼び出し側で扱う。
 */
import type { LatLng } from "./geoUtils";

export const PREF_CENTROIDS: Readonly<Record<string, LatLng>> = {
  // 北海道
  北海道: { lat: 43.064, lng: 141.347 },
  // 東北
  青森県: { lat: 40.824, lng: 140.74 },
  岩手県: { lat: 39.704, lng: 141.153 },
  宮城県: { lat: 38.269, lng: 140.872 },
  秋田県: { lat: 39.719, lng: 140.102 },
  山形県: { lat: 38.24, lng: 140.364 },
  福島県: { lat: 37.75, lng: 140.468 },
  // 関東
  茨城県: { lat: 36.342, lng: 140.447 },
  栃木県: { lat: 36.566, lng: 139.884 },
  群馬県: { lat: 36.391, lng: 139.06 },
  埼玉県: { lat: 35.857, lng: 139.649 },
  千葉県: { lat: 35.605, lng: 140.123 },
  東京都: { lat: 35.689, lng: 139.692 },
  神奈川県: { lat: 35.448, lng: 139.642 },
  // 中部
  新潟県: { lat: 37.902, lng: 139.023 },
  富山県: { lat: 36.695, lng: 137.211 },
  石川県: { lat: 36.595, lng: 136.626 },
  福井県: { lat: 36.065, lng: 136.222 },
  山梨県: { lat: 35.664, lng: 138.568 },
  長野県: { lat: 36.651, lng: 138.181 },
  岐阜県: { lat: 35.391, lng: 136.722 },
  静岡県: { lat: 34.977, lng: 138.383 },
  愛知県: { lat: 35.18, lng: 136.907 },
  // 関西
  三重県: { lat: 34.73, lng: 136.509 },
  滋賀県: { lat: 35.005, lng: 135.869 },
  京都府: { lat: 35.021, lng: 135.756 },
  大阪府: { lat: 34.686, lng: 135.52 },
  兵庫県: { lat: 34.691, lng: 135.183 },
  奈良県: { lat: 34.685, lng: 135.833 },
  和歌山県: { lat: 34.226, lng: 135.168 },
  // 中国
  鳥取県: { lat: 35.504, lng: 134.238 },
  島根県: { lat: 35.472, lng: 133.051 },
  岡山県: { lat: 34.662, lng: 133.935 },
  広島県: { lat: 34.397, lng: 132.46 },
  山口県: { lat: 34.186, lng: 131.471 },
  // 四国
  徳島県: { lat: 34.066, lng: 134.559 },
  香川県: { lat: 34.34, lng: 134.043 },
  愛媛県: { lat: 33.842, lng: 132.766 },
  高知県: { lat: 33.56, lng: 133.531 },
  // 九州 / 沖縄
  福岡県: { lat: 33.607, lng: 130.418 },
  佐賀県: { lat: 33.249, lng: 130.3 },
  長崎県: { lat: 32.745, lng: 129.874 },
  熊本県: { lat: 32.79, lng: 130.742 },
  大分県: { lat: 33.238, lng: 131.613 },
  宮崎県: { lat: 31.911, lng: 131.424 },
  鹿児島県: { lat: 31.56, lng: 130.558 },
  沖縄県: { lat: 26.212, lng: 127.681 },
};

/** 中間点: pref が未設定の店舗をソート末尾に追いやるための遠距離値。 */
export const UNKNOWN_PREF_DISTANCE_KM = 99_999;
